export function Card({
  children,
  style,
  onClick,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        borderRadius: '0.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '1rem',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Table({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        {children}
      </table>
    </div>
  );
}

export function Th({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '0.75rem',
        borderBottom: '1px solid #e5e7eb',
        fontWeight: 600,
        color: '#374151',
        ...style,
      }}
    >
      {children}
    </th>
  );
}

export function Td({ children, style, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', ...style }} {...props}>
      {children}
    </td>
  );
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}) {
  const colors = {
    primary: { bg: '#2563eb', hover: '#1d4ed8', color: '#fff' },
    secondary: { bg: '#e5e7eb', hover: '#d1d5db', color: '#111827' },
    danger: { bg: '#dc2626', hover: '#b91c1c', color: '#fff' },
  };
  const c = colors[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: c.bg,
        color: c.color,
        border: 'none',
        borderRadius: '0.375rem',
        padding: '0.5rem 1rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        fontWeight: 500,
      }}
    >
      {children}
    </button>
  );
}

export function Input({
  label,
  ...props
}: { label?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  const inputId = props.id ?? props.name;
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      {label && (
        <label
          htmlFor={inputId}
          style={{
            display: 'block',
            marginBottom: '0.25rem',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        {...props}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          boxSizing: 'border-box',
          ...props.style,
        }}
      />
    </div>
  );
}

export function Select({
  label,
  children,
  ...props
}: { label?: string } & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const selectId = props.id ?? props.name;
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      {label && (
        <label
          htmlFor={selectId}
          style={{
            display: 'block',
            marginBottom: '0.25rem',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {label}
        </label>
      )}
      <select
        id={selectId}
        {...props}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: '0.375rem',
          fontSize: '0.875rem',
          background: '#fff',
          ...props.style,
        }}
      >
        {children}
      </select>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <Card
        style={{ width: '100%', maxWidth: '28rem', margin: '1rem' }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <h3 id="modal-title" style={{ marginTop: 0 }}>
          {title}
        </h3>
        {children}
      </Card>
    </div>
  );
}

export function Badge({
  children,
  color = 'gray',
}: {
  children: React.ReactNode;
  color?: 'green' | 'red' | 'gray' | 'blue';
}) {
  const map = {
    green: { bg: '#dcfce7', color: '#166534' },
    red: { bg: '#fee2e2', color: '#991b1b' },
    gray: { bg: '#f3f4f6', color: '#374151' },
    blue: { bg: '#dbeafe', color: '#1e40af' },
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 500,
        background: map[color].bg,
        color: map[color].color,
      }}
    >
      {children}
    </span>
  );
}

export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
      <Button variant="secondary" onClick={() => onChange(page - 1)} disabled={page <= 1}>
        Sebelumnya
      </Button>
      <span style={{ padding: '0.5rem' }}>
        {page} / {totalPages}
      </span>
      <Button variant="secondary" onClick={() => onChange(page + 1)} disabled={page >= totalPages}>
        Berikutnya
      </Button>
    </div>
  );
}

export function Alert({
  children,
  type = 'info',
}: {
  children: React.ReactNode;
  type?: 'info' | 'error' | 'success';
}) {
  const map = {
    info: { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
    error: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
    success: { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  };
  return (
    <div
      style={{
        padding: '0.75rem 1rem',
        borderRadius: '0.375rem',
        background: map[type].bg,
        color: map[type].color,
        border: `1px solid ${map[type].border}`,
        marginBottom: '1rem',
      }}
    >
      {children}
    </div>
  );
}
